import { t as msg, countText } from "../lib/i18n";
import { useEffect, useMemo, useState } from "react";
import type {
  Draft,
  Template,
  User,
} from "../../../../packages/shared/src/types";
import { CardPreview } from "../components/CardPreview";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import { draftStore } from "../lib/drafts";
import { ConfirmDialog } from "../components/ConfirmDialog";
export function Templates({
  templates,
  loading,
  user,
  accountReady,
}: {
  templates: Template[];
  loading: boolean;
  user: User | null;
  accountReady: boolean;
}) {
  const store = useMemo(() => draftStore(user?.id), [user?.id]);
  const [drafts, setDrafts] = useState<Draft[]>([]),
    [remove, setRemove] = useState<Draft | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const friend = new URLSearchParams(location.search).get("friend");
  const link = (id: string) =>
    `/create?template=${id}${friend ? `&friend=${encodeURIComponent(friend)}` : ""}`;
  useEffect(() => {
    let live = true;
    setDrafts([]);
    if (accountReady)
      void store
        .all()
        .then((items) => {
          if (live) setDrafts(items.filter((d) => !d.sentCardId));
        })
        .catch((e) => {
          if (live) setError(e.message);
        });
    return () => {
      live = false;
    };
  }, [store, accountReady]);
  async function deleteDraft() {
    if (!remove || busy) return;
    setBusy(true);
    try {
      await store.remove(remove.id);
      setDrafts((items) => items.filter((d) => d.id !== remove.id));
      setRemove(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const [category, setCategory] = useState("all");
  const visible = templates.filter(
    (t) => category === "all" || t.category === category,
  );
  return (
    <section className="wrap section create-workspace">
      <p className="eyebrow">
        {msg(
          friend ? "FOR SOMEONE YOU KNOW" : "A LITTLE SOMETHING FOR EVERYONE",
        )}
      </p>
      <h1 className="page-title">{msg("What will you promise?")}</h1>
      <div className="create-intro">
        <p className="intro">
          {msg(
            "A starting point for your good intentions. Make every word your own.",
          )}
        </p>
        <a className="button secondary" href={link("blank-card")}>
          <Plus size={18} />
          {msg("Write your own")}
        </a>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!friend && drafts.length > 0 && (
        <section className="workspace-drafts">
          <div className="section-heading">
            <h2>{msg("Drafts")}</h2>
            <span className="muted">{msg("Saved on this device")}</span>
          </div>
          <div className="draft-grid">
            {drafts.map((d) => (
              <article className="draft-tile" key={d.id}>
                <a href={`/create?draft=${d.id}`}>
                  <span
                    className={`draft-swatch ${d.theme}`}
                    aria-hidden="true"
                  />
                  <span>
                    <strong>{d.title || msg("An unwritten promise")}</strong>
                    <small>
                      {msg(
                        d.clientRequestId ? "Recover send" : "Keep creating",
                      )}
                    </small>
                  </span>
                  <ArrowRight size={18} />
                </a>
                <button
                  className="icon-button"
                  aria-label={msg("Delete draft")}
                  onClick={() => {
                    setError("");
                    setRemove(d);
                  }}
                >
                  <Trash2 size={17} />
                </button>
              </article>
            ))}
          </div>
        </section>
      )}
      {remove && (
        <ConfirmDialog
          title={msg("Delete draft?")}
          confirmLabel={msg("Delete draft")}
          cancelLabel={msg("Cancel")}
          busy={busy}
          onConfirm={() => void deleteDraft()}
          onCancel={() => setRemove(null)}
        >
          {msg("This removes the draft from this device.")}
          {error && (
            <span className="error" role="alert">
              {error}
            </span>
          )}
        </ConfirmDialog>
      )}
      <div className="filters" aria-label={msg("Filter cards")}>
        {["all", "couple", "family", "friends", "blank"].map((c) => (
          <button
            key={c}
            aria-pressed={category === c}
            className={category === c ? "active" : ""}
            onClick={() => setCategory(c)}
          >
            {
              {
                all: msg("All cards"),
                couple: msg("For your person"),
                family: msg("For family"),
                friends: msg("For friends"),
                blank: msg("Your own idea"),
              }[c]
            }
          </button>
        ))}
      </div>
      {loading ? (
        <p className="loading-state" role="status">
          {msg("Getting your stationery ready…")}
        </p>
      ) : (
        <p className="template-result" role="status">
          {countText("templateCount", visible.length)}
        </p>
      )}
      <div className="template-grid" aria-busy={loading}>
        {visible.map((t) => (
          <a className="template-option" key={t.id} href={link(t.id)}>
            <CardPreview card={t} art={t.art} />
            <div className="template-caption">
              <h3>{t.name}</h3>
              <span>
                {msg("Make it yours")}
                <ArrowRight size={15} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
